import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Users, Copy, Check, QrCode, ArrowRight, Heart } from 'lucide-react';
import Card from '../shared/Card';
import Button from '../shared/Button';
import QRCode from 'qrcode';

/**
 * Shared Assessment Modal
 * Handles starting a shared assessment or joining an existing one
 */
const SharedAssessmentModal = ({ isOpen, onClose, mode, onStart, onJoin, sessionCode: existingCode }) => {
  const [step, setStep] = useState(mode === 'join' ? 'enter-code' : 'enter-name'); // enter-name, show-code, enter-code, joining
  const [name, setName] = useState('');
  const [sessionCode, setSessionCode] = useState(existingCode || '');
  const [copiedCode, setCopiedCode] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // The modal stays mounted between opens, so the mount-time step goes stale
  // once the caller switches between start and join modes — re-derive it on
  // every open.
  React.useEffect(() => {
    if (isOpen) {
      setStep(mode === 'join' ? 'enter-code' : 'enter-name');
      setError('');
    }
  }, [isOpen, mode]);

  // Keep a code handed in later (e.g. from a join link) in sync
  React.useEffect(() => {
    if (existingCode) {
      setSessionCode(existingCode.toUpperCase());
    }
  }, [existingCode]);

  // Generate QR code when we have a session code
  React.useEffect(() => {
    if (sessionCode && step === 'show-code') {
      const shareUrl = `${window.location.origin}/assessment/values-alignment/join/${sessionCode}`;
      QRCode.toDataURL(shareUrl, { width: 256, margin: 2 })
        .then(url => setQrCodeUrl(url))
        .catch(err => console.error('Failed to generate QR code:', err));
    }
  }, [sessionCode, step]);

  const handleStartShared = async () => {
    if (!name.trim()) {
      setError('Please enter your name');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await onStart(name.trim());
      if (result.success) {
        setSessionCode(result.sessionCode);
        setStep('show-code');
      } else {
        setError(result.error || 'Failed to create shared assessment');
      }
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleJoinShared = async () => {
    if (!sessionCode.trim()) {
      setError('Please enter a session code');
      return;
    }

    if (!name.trim()) {
      setError('Please enter your name');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await onJoin(sessionCode.trim().toUpperCase(), name.trim());
      if (result.success) {
        // Parent component will handle navigation
        onClose();
      } else {
        setError(result.error || 'Failed to join shared assessment');
      }
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(sessionCode);
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
    } catch (err) {
      console.error('Failed to copy code:', err);
    }
  };

  const copyShareLink = async () => {
    const shareUrl = `${window.location.origin}/assessment/values-alignment/join/${sessionCode}`;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  };

  if (!isOpen) return null;

  // Portal to <body> so the fixed header (and any transformed ancestor)
  // can't sit on top of the dialog on small screens
  return createPortal(
    <AnimatePresence>
      <div className="fixed inset-0 z-[100] overflow-y-auto" role="dialog" aria-modal="true" aria-label={mode === 'join' ? 'Join your partner\'s assessment' : 'Take the assessment with your partner'}>
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/70 backdrop-blur-sm"
          onClick={onClose}
        />

        <div
          className="relative flex min-h-full items-center justify-center p-4"
          onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="relative w-full max-w-lg"
          >
            <Card className="p-6 sm:p-8">
              {/* Close button */}
              <button
                onClick={onClose}
                aria-label="Close"
                className="absolute top-4 right-4 p-2 text-slate-400 hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>

              {/* Enter Name (Start Shared) */}
              {step === 'enter-name' && (
                <div className="space-y-6">
                  <div className="text-center">
                    <div className="w-16 h-16 bg-gradient-to-br from-rose-500 to-pink-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <Users className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-2xl font-bold text-white mb-2">
                      Take Together with Your Partner
                    </h2>
                    <p className="text-slate-300">
                      Start a shared session and invite your partner to take the Values Alignment assessment together
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Your Name
                    </label>
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleStartShared()}
                      placeholder="Enter your name"
                      className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-rose-500"
                      autoFocus
                    />
                  </div>

                  {error && (
                    <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
                      {error}
                    </div>
                  )}

                  <Button
                    onClick={handleStartShared}
                    disabled={loading || !name.trim()}
                    className="w-full"
                  >
                    {loading ? 'Creating Session...' : 'Create Shared Session'}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              )}

              {/* Show Code (Invitation) */}
              {step === 'show-code' && (
                <div className="space-y-6">
                  <div className="text-center">
                    <div className="w-16 h-16 bg-gradient-to-br from-rose-500 to-pink-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <Heart className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-2xl font-bold text-white mb-2">
                      Invite Your Partner
                    </h2>
                    <p className="text-slate-300">
                      Share this code or QR code with your partner to join
                    </p>
                  </div>

                  {/* Session Code */}
                  <div className="bg-slate-800 rounded-xl p-6 border-2 border-dashed border-slate-700">
                    <p className="text-sm text-slate-400 text-center mb-2">Session Code</p>
                    <div className="flex items-center justify-center gap-3">
                      <span className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-rose-500 to-pink-500 tracking-wider">
                        {sessionCode}
                      </span>
                      <button
                        onClick={copyCode}
                        className="p-2 text-slate-400 hover:text-white transition-colors"
                        title="Copy code"
                      >
                        {copiedCode ? <Check className="w-5 h-5 text-green-400" /> : <Copy className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>

                  {/* QR Code */}
                  {qrCodeUrl && (
                    <div className="flex flex-col items-center gap-3">
                      <div className="bg-white p-4 rounded-xl">
                        <img src={qrCodeUrl} alt="QR Code" className="w-48 h-48" />
                      </div>
                      <p className="text-xs text-slate-400 text-center">
                        Your partner can scan this QR code to join
                      </p>
                    </div>
                  )}

                  {/* Share Link */}
                  <div>
                    <Button
                      variant="secondary"
                      onClick={copyShareLink}
                      className="w-full"
                    >
                      {copiedCode ? (
                        <>
                          <Check className="w-4 h-4 mr-2" />
                          Link Copied!
                        </>
                      ) : (
                        <>
                          <Copy className="w-4 h-4 mr-2" />
                          Copy Share Link
                        </>
                      )}
                    </Button>
                  </div>

                  <div className="pt-4 border-t border-slate-700">
                    <Button
                      onClick={onClose}
                      className="w-full"
                    >
                      Start Assessment
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                    <p className="text-xs text-slate-400 text-center mt-3">
                      You can start now. Your partner can join anytime. You&apos;ll each see the
                      other&apos;s scores when you compare, and both sets of answers are stored in the shared session. For your privacy, the session
                      closes 7 days after your last activity and its answers can no longer be opened.
                    </p>
                  </div>
                </div>
              )}

              {/* Enter Code (Join) */}
              {step === 'enter-code' && (
                <div className="space-y-6">
                  <div className="text-center">
                    <div className="w-16 h-16 bg-gradient-to-br from-rose-500 to-pink-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <Users className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-2xl font-bold text-white mb-2">
                      Join Your Partner
                    </h2>
                    <p className="text-slate-300">
                      Enter the session code your partner shared with you
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Session Code
                    </label>
                    <input
                      type="text"
                      value={sessionCode}
                      onChange={(e) => setSessionCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8))}
                      placeholder="Enter session code"
                      autoComplete="off"
                      className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white text-center text-2xl font-bold tracking-wider placeholder-slate-500 focus:outline-none focus:border-rose-500"
                      autoFocus
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">
                      Your Name
                    </label>
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleJoinShared()}
                      placeholder="Enter your name"
                      className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-rose-500"
                    />
                  </div>

                  {error && (
                    <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
                      {error}
                    </div>
                  )}

                  <Button
                    onClick={handleJoinShared}
                    disabled={loading || sessionCode.trim().length < 6 || !name.trim()}
                    className="w-full"
                  >
                    {loading ? 'Joining...' : 'Join Session'}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              )}
            </Card>
          </motion.div>
        </div>
      </div>
    </AnimatePresence>,
    document.body
  );
};

export default SharedAssessmentModal;
